/**
 * mfaSetup.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

//import bluebird from "bluebird";
import QRCode from "qr-code-styling";
//import moment from "moment-timezone";
import * as React from "react";

import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import SendIcon from "@mui/icons-material/Send";

import { Button, FormControl, IconButton, InputLabel, Tooltip, 
  OutlinedInput, InputAdornment } from "@mui/material";

import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { AuthType, ThirdPartyAuth } from "../models/auth";
import { authServiceFactory } from "../services/auth.service";

interface Props {
  showInternalAuth: boolean,
  userID: string,
  email: string,
  auths?: Array<ThirdPartyAuth> | undefined,
  didStartOTP(): void,
  didClearOTP(): void
}

export function MfaSetupComponent (props: Props): React.ReactElement<any, any> {
  const authService = authServiceFactory();
  const isGlyph = authService.domain.indexOf("glyphapp") >= 0;
  const [working, setWorking] = React.useState<boolean>(false);
  const [uriData, setUriData] = React.useState<{uri: string, secret: string}>({uri: "", secret: ""});
  const [otp, setOTP] = React.useState<string>("");
  const [portableDevices, setPortableDevices] = React.useState<Array<ThirdPartyAuth>>([]);
  const [localDevices, setLocalDevices] = React.useState<Array<ThirdPartyAuth>>([]);
  const [codeAuth, setCodeAuth] = React.useState<ThirdPartyAuth | null>(null);
  const qrCodeRef = React.useRef(null);
  const [errMsg, setErrMsg] = React.useState("");
  const [qrCode, ] = React.useState<QRCode>(new QRCode({
    width: 300,
    height: 300,
    backgroundOptions: {
      color: "#FFFFFF00"
    },
    cornersSquareOptions: {
      type: "extra-rounded"
    },
    dotsOptions: {
      //type: "extra-rounded",
      gradient: {
        type: "linear",
        colorStops: isGlyph ?
          [{offset: 0, color: "#0070C0"}, {offset: 1, color: "#0070C0"}] :
          [{offset: 0, color: "#f24341"}, {offset: 1, color: "#db38a6"}]
      }
    },
    imageOptions: {
      margin: 20
    }
  }));

  React.useEffect(function () {
    const portables: Array<ThirdPartyAuth> = [];
    const device: Array<ThirdPartyAuth> = [];
    let code: ThirdPartyAuth | null = null;

    if (props.auths) {
      for (const auth of props.auths) {
        if (auth.idtype === AuthType.webauthn) {
          if (auth.attachment === "cross-platform") {
            portables.push(auth);
          } else {
            device.push(auth);
          }
        } else if (auth.idtype === AuthType.googAuth ||
            auth.idtype === AuthType.totp) {
          code = auth;
        }
      }
    }

    setPortableDevices(portables);
    setLocalDevices(device);
    setCodeAuth(code);
  }, [props.auths, props.auths?.length]);

  React.useEffect(function () {
    qrCode.update({data: uriData.uri});
    const elem = document.getElementById("qrcode-viewer");
    if (elem) { qrCode.append(elem); }
  }, [uriData]);

  async function setupWebAuthn() {
    if (working) { return; }
    try {
      const membership = authService.currentAccountMembership;
      if (!membership) { return; }
      if (!membership.email) { return; }
      const result = await authService.createLocalCredential(membership.userID,
        membership.email);
      if (result.attachment && result.attachment === "cross-platform") {
        const newArr = Array.from(portableDevices);
        newArr.push(result);
        setPortableDevices(newArr);
      } else {
        const newArr = Array.from(localDevices);
        newArr.push(result);
        setLocalDevices(newArr);
      }
    } catch (err) {
      console.error("Error setting up mfa:", err);
    } finally {
      setWorking(false);
    }
  }

  function doEdit(arrToEdit: "portables" | "locals" | "codes", index: number) {
    if (arrToEdit === "portables") {
      const arr = Array.from(portableDevices);
      arr[index].editing = true;
      arr[index].originalName = arr[index].authname;
      setPortableDevices(arr);
    } else if (arrToEdit === "locals") {
      const arr = Array.from(localDevices);
      arr[index].editing = true;
      arr[index].originalName = arr[index].authname;
      setLocalDevices(arr);
    }
  }

  function cancelAuthEdit(arrToEdit: "portables" | "locals" | "codes", index: number, event: React.SyntheticEvent) {
    event.stopPropagation();
    if (arrToEdit === "portables") {
      const arr = Array.from(portableDevices);
      arr[index].editing = false;
      arr[index].authname = arr[index].originalName as string;
      setPortableDevices(arr);
    } else if (arrToEdit === "locals") {
      const arr = Array.from(localDevices);
      arr[index].editing = false;
      arr[index].authname = arr[index].originalName as string;
      setLocalDevices(arr);
    }
  }
  function updateAuthName(arrToEdit: "portables" | "locals" | "codes", index: number, newValue: string) {
    if (arrToEdit === "portables") {
      const arr = Array.from(portableDevices);
      arr[index].authname = newValue;
      setPortableDevices(arr);
    } else if (arrToEdit === "locals") {
      const arr = Array.from(localDevices);
      arr[index].authname = newValue;
      setLocalDevices(arr);
    }
  }
  async function authNameKeyUp(arrToEdit: "portables" | "locals" | "codes", index: number, event: React.KeyboardEvent) {
    if (event.key !== "Enter") { return; }
    event.stopPropagation();
    event.preventDefault();

    let authToEdit: ThirdPartyAuth | null = null;
    if (arrToEdit === "portables") {
      const arr = Array.from(portableDevices);
      arr[index].editing = false;
      authToEdit = arr[index];
      setPortableDevices(arr);
    } else if (arrToEdit === "locals") {
      const arr = Array.from(localDevices);
      arr[index].editing = false;
      authToEdit = arr[index];
      setLocalDevices(arr);
    }

    if (!authToEdit) { return; }
    try {
      await authService.updateAuth(authToEdit._id, {authname: authToEdit.authname});
    } catch (err) {
      console.error("Error saving authname:", err);
      setErrMsg((err as Error).message);
    }
  }

  async function setupGoogleAuth () {
    const authService = authServiceFactory();
    if (working) { return; }
    try {
      const u = await authService.getOTPSecret("authenticator", props.email);
      setUriData(u);
      props.didStartOTP();
    } catch (err) {
      console.error("Error setting up mfa:", err);
    } finally {
      setWorking(false);
    }
  }

  async function confirmAuth () {
    const authService = authServiceFactory();
    if (working) { return; }
    try {
      const membership = authService.currentAccountMembership;
      if (!membership) { return; }
      if (!membership.email) { return; }
      const auth = await authService.confirmOTP(
        "authenticator", membership.email, otp, uriData.secret);

      setUriData({uri: "", secret: ""});
      setCodeAuth(auth);
      props.didClearOTP();
    } catch (err) {
      console.error("Error setting up mfa:", err);
    } finally {
      setWorking(false);
    }
  }

  function clearOTP() {
    console.log("clear OTP")
    setUriData({uri: "", secret: ""});
    props.didClearOTP();
  }

  async function removeAuth(auth: ThirdPartyAuth, event: React.SyntheticEvent) {
    event.stopPropagation();
    try {
      await authServiceFactory().removeAuth(auth._id);
      if (codeAuth && codeAuth._id === auth._id) {
        setCodeAuth(null);
        return;
      }
      let index = localDevices.findIndex(a => a._id === auth._id);
      if (index >= 0) {
        const newArr = Array.from(localDevices);
        newArr.splice(index, 1);
        setLocalDevices(newArr);
        return;
      }
      index = portableDevices.findIndex(a => a._id === auth._id);
      if (index >= 0) {
        const newArr = Array.from(portableDevices);
        newArr.splice(index, 1);
        setPortableDevices(newArr);
        return;
      }
    } catch (err) {
      console.error("Error removing auth:", err);
    }
  }

  function onKeyUp(event: React.KeyboardEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (event.key === "Enter") {
      event.stopPropagation();
      event.preventDefault();
      confirmAuth();
    }
  }

  function renderOTPForm(): React.ReactElement<any, any> {
    return <>
      <div>
        <p>Scan this barcode in the Google Authenticator App:</p>
        <div id="qrcode-viewer" ref={qrCodeRef}></div>
      </div>

      <p>Enter the current code:</p>
      <div className="form-row">
        <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="otp-input">Code</InputLabel>
          <OutlinedInput
            id="otp-input"
            type="text"
            name="otp-code"
            size="small"
            autoComplete="one-time-code"
            label="Code"
            value={otp}
            onChange={event => setOTP(event.target.value)}
            onKeyUp={onKeyUp}
            startAdornment={
              <InputAdornment position="start">
                <IconButton onClick={clearOTP}><ClearIcon /></IconButton>
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={confirmAuth}><SendIcon /></IconButton>
              </InputAdornment>
            } />
        </FormControl>
      </div>
    </>;
  }

  return <div className="mfa-container">
    <div className="button-row">
        <Button variant="contained" color="secondary"
            disableRipple disableTouchRipple
            onClick={setupWebAuthn}>
          Add Passkey / Local
        </Button>
      </div>
    <section>
      <h3>
        <span>Passkeys</span>
        <Tooltip className="info-tooltip" title={
            `Passkeys are cryptographic identities stored on your devices, e.g. your smartphone or
            a Yubikey device, which we can use to log you in.`} arrow>
            <InfoIcon className="info" />
        </Tooltip>
      </h3>
      <div className="list-with-right-controls">
        {portableDevices.map((auth, index) => <div className="item-with-right-controls" key={auth._id} onClick={() => doEdit("portables", index)}>
          {auth.editing ?
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor={`auth-name-${auth._id}`}>Authenticator Name</InputLabel>
              <OutlinedInput
                id={`auth-name-${auth._id}`}
                type="text"
                name="auth-name"
                size="small"
                label="Authenticator Name"
                value={auth.authname}
                onChange={event => updateAuthName("portables", index, event.target.value)}
                onKeyUp={event => authNameKeyUp("portables", index, event)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton aria-label="cancel-edit" 
                      onClick={event => cancelAuthEdit("portables", index, event)}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                } />
            </FormControl>:
            <div className="item-content">{auth.authname || <i>Unnamed</i>}</div>}
          <span className="right-controls">
              <IconButton className="remove-btn" size="small"
                  onClick={event => removeAuth(auth, event)}>
                <Tooltip title="Remove Device" arrow>
                  <i className="fas fa-minus-circle"></i>
                </Tooltip>
              </IconButton>
              </span>
        </div>)}
      </div>
    </section>

    {props.showInternalAuth && <section>
      <h3>
        <span>Local Authenticators</span>
        <Tooltip className="info-tooltip" title={
          `These authenticators are built into your device, e.g.
            Face ID, Touch ID, or Windows Hello.`} arrow>
          <InfoIcon className="info" />
        </Tooltip>
      </h3>
      <div className="list-with-right-controls">
        {localDevices.map(auth => <div className="item-with-right-controls" key={auth._id}>
          <div className="item-content">{auth.authname || <i>Unnamed</i>}</div>
          <span className="right-controls">
            <IconButton className="remove-btn" size="small"
                disableRipple disableTouchRipple
                onClick={evt => removeAuth(auth, evt)}>
              <Tooltip title="Remove Device" arrow>
                <i className="fas fa-minus-circle"></i>
              </Tooltip>
            </IconButton>
          </span>
        </div>)}
      </div>
    </section>}

    <section>
      <h3>
        <span>Code Authenticator</span>
        <Tooltip className="info-tooltip" title={
          `These are apps or devices that provide a rotating code to use at login.
           We currently only support the Google Authenticator App.`} arrow>
          <InfoIcon className="info" />
        </Tooltip>
      </h3>
      {codeAuth ?
        <div className="list-with-right-controls">
          <div className="item-with-right-controls">
            <div className="item-content">{codeAuth.authname || <i>Unnamed</i>}</div>
            <span className="right-controls">
              <IconButton className="remove-btn" size="small"
                  disableRipple disableTouchRipple
                  onClick={evt => removeAuth(codeAuth, evt)}>
                <Tooltip title="Remove Device" arrow>
                  <i className="fas fa-minus-circle"></i>
                </Tooltip>
              </IconButton>
            </span>
          </div>
        </div> :
        uriData.uri ?
          renderOTPForm() :
          <div className="button-row">
            <Button variant="contained" color="secondary"
                disableRipple disableTouchRipple
                onClick={setupGoogleAuth}>
              Setup OTP Authenticator
            </Button>
          </div>}
    </section>
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") } />
  </div>;
}
